// src/belediye/pages/notifications/NotificationsPage.js
//
// Bildirim Merkezi (Rotalı Sayfa)
// - Navbar dropdown: son 15
// - Bu sayfa: tam liste + arama/filtre + “Daha fazla yükle”
// - Provider-agnostic tasarım (şimdilik close_approval provider)
//
// Varsayımlar:
// - store.js içinde belediyeNotifications slice mevcut (senin güncel store’unda var)
// - slice exportları:
//   fetchNotifications, markRead, markOneAsReadLocal, hydrateReadIdsFromStorage
//   selectNotificationsForActiveUnit, selectUnreadCountForActiveUnit
//   selectNotificationsLoading, selectNotificationsError, selectNotificationsMarkingRead
//
// Notlar:
// - close_request.cancelled event’i slice içinde zaten gizleniyor (shouldHideNotification).
// - “Okundu” bilgisi localStorage + backend mark-read ile kalıcı.
// - Performans: sayfa açılışında 150 çekiyoruz; “Daha fazla” ile 300/500… replace.
// - “Loading zıplaması” istenmiyorsa slice fetchNotifications’a silent desteği eklenebilir.
//   (Bu sayfada manual fetch’lerde silent=false, background refresh’te silent=true kullanıyoruz.)

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronRightIcon,
  BellAlertIcon,
  Cog6ToothIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";

import {
  fetchNotifications,
  markRead,
  markAllRead,
  markOneAsReadLocal,
  hydrateReadIdsFromStorage,
  selectNotificationsForActiveUnit,
  selectUnreadCountForActiveUnit,
  selectUnreadBySource,
  selectNotificationsLoading,
  selectNotificationsError,
  selectNotificationsMarkingRead,
} from "../../redux/slices/belediyeNotificationsSlice";


import usePermissions from "../../hooks/usePermissions";
// FAZ-52: modül kapalıysa o kaynağın sekmesi hiç görünmesin
import useModuleEnabled from "../../hooks/useModuleEnabled";
import PageHeader from "../../components/utils/PageHeader";
import SearchBox from "../../components/utils/SearchBox";
import FilterSheet from "../../components/utils/FilterSheet";
import DuyuruModal from "../../components/notifications/DuyuruModal";
import SettingsModal from "../../components/notifications/SettingsModal";
import {
  levelLabel,
  sourceLabel,
  actorName,
  bildirimGovdesi,
  // FAZ-85: "hangi kayıt" ve "ne yazıldı" ayrı ayrı okunur.
  kayitEtiketi,
  bildirimNotu,
  eventOptionsForSegment,
  segmentSourceParam,
  segmentUnreadFromSources,
  SEGMENTS,
  matchesSegment,
} from "../../components/notifications/notificationMeta";
import resolveNotificationTarget from "../../components/notifications/resolveNotificationTarget";
// FAZ-51: satır ikonu kaynağı anlatır (mesaj sohbet türüyle, görev iş çantasıyla)
import { sourceIcon, conversationIcon, DuyuruIcon } from "../../components/icons/appIcons";
// FAZ-51: mesaj bildirimi tıklanınca sohbet DOCK'ta yerinde açılır (rota değişmez)
import { openChat } from "../../services/mesajlasmaBridge";
import "./NotificationsPage.css";

const LS = {
  user: "user",
  activeUnitSlug: "activeUnitSlug",
};

function safeJsonParse(v) {
  try {
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

function readActiveUnitSlugSafe() {
  try {
    return safeStr(localStorage.getItem(LS.activeUnitSlug)).trim();
  } catch {
    return "";
  }
}

function pickEvent(n) {
  return safeStr(n?.event || n?.inapp_payload?.event).trim();
}

function pickTitle(n) {
  // FAZ-47: backend zaten insan-dili başlık üretiyor — zil bunu okuyordu, bu
  // sayfa okumuyordu; görev/duyuru bildirimleri "task.assigned" gibi HAM OLAY
  // KODU olarak görünüyordu. Önce backend başlığı, sonra eski eşleme.
  const backendTitle = safeStr(n?.title).trim();
  if (backendTitle) return backendTitle;
  const ev = pickEvent(n);
  if (ev.includes("close_request.created")) return "Yeni kapatma talebi";
  if (ev.includes("close_request.approved")) return "Kapatma talebi onaylandı";
  if (ev.includes("close_request.rejected")) return "Kapatma talebi reddedildi";
  // cancelled zaten slice’ta gizli
  return ev || "Bildirim";
}

// FAZ-69: gövde başlıkla aynıysa yazılmaz (tek kaynak: notificationMeta).
const pickMessage = (n) => bildirimGovdesi(n);

function pickApplicationId(n) {
  const a =
    n?.application_id ??
    n?.inapp_payload?.application_id ??
    n?.payload?.application_id ??
    null;
  const num = Number(a);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function matchesSearch(n, q) {
  const query = safeStr(q).trim().toLowerCase();
  if (!query) return true;

  const hay = [
    pickTitle(n),
    pickMessage(n),
    pickEvent(n),
    safeStr(n?.close_request_id),
    safeStr(pickApplicationId(n)),
  ]
    .join(" ")
    .toLowerCase();

  return hay.includes(query);
}

function isReadByAny(n, readIdsMap) {
  const id = Number(n?.id);
  if (!id) return true;
  return Boolean(n?.is_read) || Boolean(readIdsMap?.[id]);
}

function withinPreset(n, presetKey) {
  // presetKey: "all" | "24h" | "7d" | "30d"
  if (!presetKey || presetKey === "all") return true;

  const iso = n?.created_at_iso || n?.created_at;
  if (!iso) return true;

  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return true;

  const now = Date.now();
  const diffMs = now - t;

  const day = 24 * 60 * 60 * 1000;
  if (presetKey === "24h") return diffMs <= day;
  if (presetKey === "7d") return diffMs <= 7 * day;
  if (presetKey === "30d") return diffMs <= 30 * day;

  return true;
}

// Okunmamış önce, sonra okunanlar (id desc)
function prioritizeUnreadThenRead(items, readIdsMap) {
  const unread = [];
  const read = [];
  for (const it of Array.isArray(items) ? items : []) {
    if (!isReadByAny(it, readIdsMap)) unread.push(it);
    else read.push(it);
  }
  unread.sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  read.sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  return [...unread, ...read];
}

// Basit debounce hook (ek dependency yok)
function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), Math.max(0, Number(delayMs) || 0));
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// Seviye → ikon. FAZ-51: yalnız "acil/uyarı" satırlarda seviye ikonu öne çıkar;
// normal satırlarda KAYNAK ikonu gösterilir (bildirimin neyle ilgili olduğu,
// hangi tonda olduğundan daha çok bilgi taşıyor).
const LEVEL_ICON = {
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  critical: ExclamationCircleIcon,
};

/** Satırın solundaki simge: mesajda sohbet TÜRÜ, diğerlerinde kaynak ikonu. */
function rowIcon(n) {
  const src = safeStr(n?.source_module).trim();
  if (src === "mesajlasma") {
    const kind = safeStr(n?.payload?.kind || n?.inapp_payload?.kind).trim();
    return conversationIcon(kind);
  }
  return sourceIcon(src);
}

// Gün kovası (Bugün / Dün / tarih) — gruplu liste için
function dayBucket(iso) {
  if (!iso) return { key: "unknown", label: "Tarihsiz" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { key: "unknown", label: "Tarihsiz" };
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(new Date()) - startOf(d)) / 86400000);
  if (diffDays <= 0) return { key: "today", label: "Bugün" };
  if (diffDays === 1) return { key: "yesterday", label: "Dün" };
  const label = d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
  return { key: label, label };
}

function timeOnlyTR(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

export default function NotificationsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // user/role
  const user = useMemo(() => safeJsonParse(localStorage.getItem(LS.user)), []);
  const [activeUnitSlug, setActiveUnitSlug] = useState(() => readActiveUnitSlugSafe());

  // FAZ-34: duyuru gönderme yetkisi (buton görünürlüğü)
  const { permissions } = usePermissions();
  const canSendDuyuru = Array.isArray(permissions) && permissions.includes("bildirim.duyuru.gonder");

  // FAZ-52: modül kapalıysa o segment sekmesi hiç görünmez. Backend zaten
  // kapalı kaynağın satırlarını feed'e koymuyor (visible_sources) — sekme
  // kalsaydı kullanıcı sürekli boş bir sekmeye bakardı.
  const gorevModulu = useModuleEnabled("internal_tasks");
  const mesajModulu = useModuleEnabled("mesajlasma");

  // Modallar (ayrı sayfa yerine açılır pencere)
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [duyuruOpen, setDuyuruOpen] = useState(false);

  // store state
  const loading = useSelector(selectNotificationsLoading);
  const error = useSelector(selectNotificationsError);
  const markingRead = useSelector(selectNotificationsMarkingRead);

  // slice readIds map (persist edilmiş)
  const readIds = useSelector((s) => s?.belediyeNotifications?.readIds || {});
  const unreadCount = useSelector((s) => selectUnreadCountForActiveUnit(s, activeUnitSlug));
  // FAZ-77: sekme rozetleri artık backend'in kaynak-başı sayacından.
  const unreadBySource = useSelector(selectUnreadBySource);

  // raw list (aktif birime göre – fail-safe)
  const items = useSelector((s) => selectNotificationsForActiveUnit(s, activeUnitSlug));

  // ---------- UI State ----------
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 320);

  // FAZ-51 — sekmeler artık KAYNAK segmentleri (Tümü / Mesajlar / Görevler /
  // Başvurular / Duyuru & Sistem). Okundu-durumu ayrı bir eksene taşındı
  // (aşağıdaki onlyUnread anahtarı) — eskiden ikisi aynı sekme satırını
  // paylaştığı için "görev bildirimlerimin okunmamışları" gibi en sık ihtiyaç
  // hiç kurulamıyordu.
  const [segment, setSegment] = useState("all");
  const [onlyUnread, setOnlyUnread] = useState(false);

  const gorunurSegmentler = useMemo(
    () =>
      SEGMENTS.filter((s) => {
        if (s.key === "gorev") return gorevModulu;
        if (s.key === "mesajlasma") return mesajModulu;
        return true;
      }),
    [gorevModulu, mesajModulu]
  );

  // Açık sekme kapanan bir modüle aitse "Tümü"ne düş (boş ekranda kalmasın).
  useEffect(() => {
    if (!gorunurSegmentler.some((s) => s.key === segment)) setSegment("all");
  }, [gorunurSegmentler, segment]);

  // event filter
  const [eventFilter, setEventFilter] = useState("all");

  // FAZ-77: kategori seçenekleri AÇIK SEKMEYE göre. Eskiden 20 olayın tamamı
  // listeleniyordu; "Görevler" sekmesinde "Kapatma Talebi • Onay" seçilebiliyor
  // ve sonuç garanti boş ekrandı (iki süzgeç birbirini imkânsız kılıyor).
  const eventOptions = useMemo(() => eventOptionsForSegment(segment), [segment]);

  // Sekme değişince, artık mümkün olmayan kategori seçimi kendiliğinden düşer.
  useEffect(() => {
    if (eventFilter === "all") return;
    if (!eventOptions.some((o) => o.value === eventFilter)) setEventFilter("all");
  }, [eventOptions, eventFilter]);

  // date preset
  const [datePreset, setDatePreset] = useState("all"); // "all" | "24h" | "7d" | "30d"

  // ordering
  const [unreadFirst, setUnreadFirst] = useState(true);

  // Filtre rozeti sayısı (FilterSheet activeCount)
  const activeFilterCount =
    (eventFilter !== "all" ? 1 : 0) + (datePreset !== "all" ? 1 : 0) + (!unreadFirst ? 1 : 0);

  // fetch limit for page
  const [limit, setLimit] = useState(150);

  const canRender = Boolean(user);

  // storage sync (unit değişince)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === LS.activeUnitSlug) setActiveUnitSlug(readActiveUnitSlugSafe());
      if (e.key === LS.user) {
        // kullanıcı değişimi varsa readIds key’i değişmiş olabilir
        dispatch(hydrateReadIdsFromStorage());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatch]);

  // first hydrate
  useEffect(() => {
    if (!canRender) return;
    dispatch(hydrateReadIdsFromStorage());
  }, [dispatch, canRender]);

  // FAZ-77 — SÜZME SUNUCUDA. Sekme değişince o kaynağın listesi baştan çekilir
  // (`source` parametresi). Eskiden tek bir "son 150 satır" penceresi çekilip
  // sekmeler o pencerede yerel süzülüyordu: "Görevler" sekmesi, son 150 satıra
  // kaç görev bildirimi denk geldiyse onu gösteriyordu — sekmenin listesi
  // sistematik olarak eksikti.
  const fetchPage = useCallback(
    async ({ nextLimit, silent = false, segmentKey } = {}) => {
      const finalLimit = Math.max(20, Math.min(Number(nextLimit || limit) || limit, 500));
      const seg = segmentKey === undefined ? segment : segmentKey;

      const arg = {
        unreadOnly: false,
        limit: finalLimit,
        mode: "replace",
        source: segmentSourceParam(seg),
        ...(silent ? { silent: true } : {}),
      };

      await dispatch(fetchNotifications(arg)).unwrap?.();
      setLimit(finalLimit);
    },
    [dispatch, limit, segment]
  );

  // page init — FAZ-65: 30 SANİYELİK ARKA PLAN YENİLEMESİ KALDIRILDI.
  //
  // "Sessiz" olması yetmiyordu: liste okunmamış-önce sıralı olduğu için her
  // tazelemede satırlar YER DEĞİŞTİRİYOR, okuduğunuz satır gözünüzün önünden
  // kayıyordu (kullanıcı: "durup durup yenileniyor, sinir bozucu").
  // Tazeleme artık yalnız: sayfa açılışı + sekmeye geri dönüş + "Yenile".
  useEffect(() => {
    if (!canRender) return undefined;

    fetchPage({ nextLimit: 150, silent: false }).catch(() => {
      // error zaten slice’a düşecek
    });

    const onVis = () => {
      if (!document.hidden) fetchPage({ nextLimit: limit, silent: true }).catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [canRender]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sekme değişimi = yeni bir SUNUCU isteği (kaynak süzgeciyle). İlk yükleme
  // yukarıdaki effect'te olduğu için burada atlanır (çift istek olmasın).
  const segmentRef = useRef(segment);
  useEffect(() => {
    if (segmentRef.current === segment) return;
    segmentRef.current = segment;
    if (!canRender) return;
    fetchPage({ nextLimit: 150, silent: false, segmentKey: segment }).catch(() => {});
  }, [segment, canRender]); // eslint-disable-line react-hooks/exhaustive-deps

  // FAZ-77: sekme rozetleri BACKEND'den (unread_by_source). Eskiden o an yüklü
  // 150 satırdan sayılıyordu → "Görevler (2)" yazarken sekmede 9 tane çıkıyordu.
  const segmentUnread = useMemo(
    () => segmentUnreadFromSources(unreadBySource, unreadCount),
    [unreadBySource, unreadCount]
  );

  // Açık sekmenin okunmamış sayısı — "Okunmamış" çipi bunu gösterir. Eskiden
  // her sekmede TOPLAM sayı yazıyordu (Duyurular sekmesinde 0 satır varken
  // çipte 12 görünüyordu).
  const aktifSegmentUnread = Number(segmentUnread[segment] || 0);

  const filtered = useMemo(() => {
    const list = Array.isArray(items) ? items : [];

    // segment (kaynak) + okundu ekseni
    let out = list.filter((n) => {
      if (!matchesSegment(n, segment)) return false;
      if (onlyUnread && isReadByAny(n, readIds)) return false;
      return true;
    });

    // event filter
    if (eventFilter !== "all") {
      out = out.filter((n) => pickEvent(n) === eventFilter);
    }

    // date preset
    out = out.filter((n) => withinPreset(n, datePreset));

    // search
    out = out.filter((n) => matchesSearch(n, debouncedQuery));

    // order
    if (unreadFirst) return prioritizeUnreadThenRead(out, readIds);

    // default: newest first (id desc)
    return [...out].sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  }, [items, readIds, segment, onlyUnread, eventFilter, datePreset, debouncedQuery, unreadFirst]);

  const handleRefresh = useCallback(() => {
    fetchPage({ nextLimit: limit, silent: false }).catch(() => {});
  }, [fetchPage, limit]);

  const handleLoadMore = useCallback(() => {
    const next = Math.min(500, limit + 150);
    fetchPage({ nextLimit: next, silent: false }).catch(() => {});
  }, [fetchPage, limit]);

  const handleClickNotification = useCallback(
    (n) => {
      const nid = Number(n?.id);
      if (!nid) return;

      // UI snappy
      dispatch(markOneAsReadLocal(nid));

      // backend persist
      dispatch(markRead({ ids: [nid] })).unwrap?.().catch(() => {
        // fail-safe: UI local yine okundu gösterir
      });

      // FAZ-47: yönlendirme ortak çözücüde. Eskiden YALNIZ application_id'ye
      // bakılıyordu → görev/duyuru bildirimi okundu oluyor ama hiçbir yere
      // gitmiyordu.
      // FAZ-51: sohbet bildirimi artık bu sayfada da DOCK'ta açılır. ChatDock
      // BelediyeApp seviyesinde mount'lu → köprü her rotadan çalışır; sayfayı
      // terk etmeden sohbete girilir (kullanıcı kararı: "tıklayınca sohbete
      // gitsin").
      const target = resolveNotificationTarget(n, { allowChat: true });
      if (!target) return;
      if (target.kind === "chat") openChat(target.conversationId);
      else navigate(target.path);
    },
    [dispatch, navigate]
  );

  // Tümünü okundu işaretle.
  // FAZ-49: GERÇEKTEN tümü — eskiden yalnız o an yüklü olan okunmamışların
  // id'leri gönderiliyordu, 150'den fazlasında buton işini yapmıyordu.
  // FAZ-77: kapsam AÇIK SEKME. Görevler sekmesindeyken düğme duyuruları da
  // okundu yapıyordu; gördüğünüz liste ile eylemin kapsamı çelişiyordu.
  const handleMarkAllRead = useCallback(() => {
    dispatch(markAllRead({ source: segmentSourceParam(segment) }))
      .unwrap?.()
      .catch(() => {});
  }, [dispatch, segment]);

  // Gün gruplu liste (Bugün / Dün / tarih)
  const groups = useMemo(() => {
    const map = new Map();
    for (const n of Array.isArray(filtered) ? filtered : []) {
      const iso = n?.created_at_iso || n?.created_at;
      const { key, label } = dayBucket(iso);
      if (!map.has(key)) map.set(key, { key, label, items: [] });
      map.get(key).items.push(n);
    }
    return Array.from(map.values());
  }, [filtered]);

  if (!canRender) {
    // login yoksa zaten route’a gelmemeli ama güvenli
    return null;
  }

  return (
    <div className="nx-page">
      <PageHeader
        title="Bildirimler"
        tabs={
          <div className="nx-tabs">
            {gorunurSegmentler.map((s) => {
              const n = segmentUnread[s.key] || 0;
              return (
                <button
                  key={s.key}
                  type="button"
                  className={`nx-tab ${segment === s.key ? "is-active" : ""}`}
                  onClick={() => setSegment(s.key)}
                >
                  {s.label}
                  {n > 0 ? (
                    <span className="nx-tab-badge">{n > 99 ? "99+" : n}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        }
        belowToolbar={
          /* FAZ-69 — ARAÇ ŞERİDİ.
             Araç çubuğunda yedi şey vardı (okunmamış anahtarı, arama, filtre,
             yenile, tümünü okundu, ayarlar, duyuru) ve mobilde hepsi etiketsiz
             ikona dönüşüp okunaksız bir sıraya diziliyordu. Kullanıcı kararı:
             okundu ekseni ve ayarlar SAYFA İÇİNE alınsın.

             Ayrım net: ÜST ÇUBUK = "neyi arıyorum" (arama, filtre, sekme) ve tek
             birincil eylem (Duyuru). BU ŞERİT = "listeye ne yapıyorum"
             (okundu süzgeci, tümünü okundu, yenile, ayarlar). */
          <div className="nx-strip">
            <div className="nx-strip-left">
              <button
                type="button"
                className={`nx-chipbtn ${!onlyUnread ? "is-active" : ""}`}
                onClick={() => setOnlyUnread(false)}
              >
                Tümü
              </button>
              <button
                type="button"
                className={`nx-chipbtn ${onlyUnread ? "is-active" : ""}`}
                onClick={() => setOnlyUnread(true)}
              >
                <BellAlertIcon />
                Okunmamış
                {/* FAZ-77: AÇIK SEKMENİN sayısı. Eskiden her sekmede toplam
                    yazıyordu → Duyurular sekmesinde hiç satır yokken çipte 12
                    görünüyordu. */}
                {aktifSegmentUnread > 0 ? (
                  <span className="nx-chipbtn-count">
                    {aktifSegmentUnread > 99 ? "99+" : aktifSegmentUnread}
                  </span>
                ) : null}
              </button>
            </div>

            <div className="nx-strip-right">
              {/* FAZ-77: düğme artık KAYBOLMUYOR, pasifleşiyor. Okunmamış
                  bitince düğmenin yok olması şeridin genişliğini değiştiriyor,
                  yanındaki "Yenile" tıklamak istediğiniz an yer değiştiriyordu.
                  Etiket de kapsamı söylüyor: sekmedeyken "Bu sekmeyi okundu". */}
              <button
                type="button"
                className="nx-striptool"
                onClick={handleMarkAllRead}
                disabled={markingRead || aktifSegmentUnread === 0}
                title={
                  segment === "all"
                    ? "Tüm bildirimleri okundu işaretle"
                    : "Bu sekmedeki bildirimleri okundu işaretle"
                }
              >
                <CheckIcon />
                <span className="nx-striptool-label">
                  {segment === "all" ? "Tümünü okundu" : "Bu sekmeyi okundu"}
                </span>
              </button>

              <button
                type="button"
                className="nx-striptool"
                onClick={handleRefresh}
                disabled={loading}
                title="Yenile"
                aria-label="Yenile"
              >
                <ArrowPathIcon className={loading ? "nx-spin" : ""} />
                <span className="nx-striptool-label">Yenile</span>
              </button>

              <button
                type="button"
                className="nx-striptool"
                onClick={() => setSettingsOpen(true)}
                title="Bildirim Ayarları"
                aria-label="Bildirim Ayarları"
              >
                <Cog6ToothIcon />
                <span className="nx-striptool-label">Ayarlar</span>
              </button>
            </div>
          </div>
        }
      >
        <SearchBox
          value={query}
          onChange={setQuery}
          onClear={() => setQuery("")}
          placeholder="Bildirimlerde ara…"
        />

        <FilterSheet
          activeCount={activeFilterCount}
          onClear={() => {
            setEventFilter("all");
            setDatePreset("all");
            setUnreadFirst(true);
          }}
          title="Filtreler"
          label="Filtre"
        >
          <div className="nx-filter-fields">
            <label className="nx-field">
              <span className="nx-field-label">Kategori</span>
              <select
                className="md-select"
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
              >
                <option value="all">Tümü</option>
                {eventOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="nx-field">
              <span className="nx-field-label">Tarih</span>
              <select
                className="md-select"
                value={datePreset}
                onChange={(e) => setDatePreset(e.target.value)}
              >
                <option value="all">Tümü</option>
                <option value="24h">Son 24 saat</option>
                <option value="7d">Son 7 gün</option>
                <option value="30d">Son 30 gün</option>
              </select>
            </label>

            <label className="nx-check">
              <input
                type="checkbox"
                checked={unreadFirst}
                onChange={(e) => setUnreadFirst(Boolean(e.target.checked))}
              />
              <span>Okunmamış önce</span>
            </label>
          </div>
        </FilterSheet>

        {/* Duyuru = tek BİRİNCİL eylem; diğer araçlar alt şeritte (bkz. belowToolbar).
            FAZ-95 madde 26 — telefonda sağ-alta yüzen YUVARLAK ikon düğmeye
            döner (konumu CSS'te, hesap FAZ-92'nin "Yeni Talep"iyle ortak).
            İkon kâğıt uçaktan MEGAFONA geçti: `DuyuruIcon` kavramın tek
            kaynağıdır (FAZ-51) ve bildirim satırlarında duyuru zaten onunla
            çiziliyordu — aynı şeyin iki ikonu vardı. Etiket masaüstünde
            kalıyor, telefonda düğme yalnız ikon. */}
        {canSendDuyuru ? (
          <button
            type="button"
            className="btn-md btn-md-primary nx-tool"
            onClick={() => setDuyuruOpen(true)}
            title="Duyuru Gönder"
            aria-label="Duyuru Gönder"
          >
            <DuyuruIcon />
            <span className="nx-tool-label">Duyuru</span>
          </button>
        ) : null}
      </PageHeader>

      <div className="nx-body">
        {error ? (
          <div className="nx-alert nx-alert--err">
            <ExclamationTriangleIcon className="nx-alert-icon" />
            <span>{safeStr(error)}</span>
          </div>
        ) : null}

        {loading && filtered.length === 0 ? (
          <div className="nx-state">
            <ArrowPathIcon className="nx-state-icon nx-spin" />
            <span>Yükleniyor…</span>
          </div>
        ) : null}

        {!loading && filtered.length === 0 ? (
          <div className="nx-empty">
            <BellAlertIcon className="nx-empty-icon" />
            <div className="nx-empty-title">Bildirim yok</div>
            <div className="nx-empty-sub">
              {debouncedQuery || activeFilterCount || onlyUnread || segment !== "all"
                ? "Filtrelerle eşleşen bildirim bulunamadı."
                : "Yeni bildirimler burada görünecek."}
            </div>
          </div>
        ) : null}

        {filtered.length > 0 ? (
          <div className="nx-list">
            {groups.map((g) => (
              <section key={g.key} className="nx-group">
                <div className="nx-group-label">{g.label}</div>
                <div className="nx-group-items">
                  {g.items.map((n) => {
                    const id = Number(n?.id);
                    if (!id) return null;
                    const isRead = isReadByAny(n, readIds);
                    const lvl = safeStr(n?.level) || "info";
                    // Acil/uyarıda seviye ikonu (dikkat çeksin), diğerlerinde
                    // kaynak ikonu (neyle ilgili olduğu daha yararlı).
                    const Icon =
                      lvl === "critical" || lvl === "warning"
                        ? LEVEL_ICON[lvl] || InformationCircleIcon
                        : rowIcon(n);
                    const who = actorName(n);
                    // FAZ-85: "hangi kayıt" ve "ne yazıldı" ayrı ayrı çizilir.
                    const kayit = kayitEtiketi(n);
                    const not_ = bildirimNotu(n);
                    const nav = resolveNotificationTarget(n, { allowChat: true });
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`nx-item ${isRead ? "is-read" : "is-unread"}`}
                        onClick={() => handleClickNotification(n)}
                        disabled={markingRead && !isRead}
                        title={nav ? "Bildirime git" : "Aç"}
                      >
                        <span className={`nx-ic nx-ic--${lvl}`}>
                          <Icon />
                        </span>
                        <span className="nx-main">
                          <span className="nx-row1">
                            <span className="nx-title">{pickTitle(n)}</span>
                            <span className="nx-time">
                              {timeOnlyTR(n?.created_at_iso || n?.created_at)}
                            </span>
                          </span>
                          {/* FAZ-85 — HANGİ KAYIT kendi satırında.
                              Eskiden "#110 · Kaldırım onarımı — beton döküldü"
                              tek gri satırda birlikte yazılıyor ve uzun başlıklı
                              bir talepte notun tamamı ellipsis'e gidiyordu. */}
                          {kayit ? (
                            <span className="nx-kayit" title={kayit}>
                              {kayit}
                            </span>
                          ) : null}
                          {not_ ? <span className="nx-msg">{not_}</span> : null}
                          <span className="nx-meta">
                            {/* FAZ-85: KİM — meta satırının sonunda soluk bir
                                ek değil, başında okunur bir öğe. */}
                            {who ? (
                              <span className="nx-who">
                                <UserCircleIcon className="nx-who-ic" />
                                {who}
                              </span>
                            ) : null}
                            <span className={`nx-chip nx-chip--${lvl}`}>{levelLabel(lvl)}</span>
                            <span className="nx-src">{sourceLabel(n?.source_module)}</span>
                          </span>
                        </span>
                        <span className="nx-tail">
                          {!isRead ? <span className="nx-unread-dot" aria-label="okunmadı" /> : null}
                          {nav ? <ChevronRightIcon className="nx-go" /> : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}

            {items.length >= limit && limit < 500 ? (
              <div className="nx-more">
                <button
                  type="button"
                  className="btn-md btn-md-outline"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  Daha fazla yükle
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      {canSendDuyuru ? (
        <DuyuruModal open={duyuruOpen} onClose={() => setDuyuruOpen(false)} onSent={() => handleRefresh()} />
      ) : null}
    </div>
  );
}